// frontend/src/pages/app/Announcements.jsx
import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../store";
import { useRole } from "../../hooks/useRole";
import { useAnnouncements } from "../../hooks/useAnnouncements";
import { Icon, EmptyState, Pagination, Avatar } from "../../components/Common";
import { deleteAnnouncement, pinAnnouncement, unpinAnnouncement } from "../../lib/api";
import AnnouncementFormModal from "../../components/announcements/AnnouncementFormModal";
import PageBoundary from "../../components/PageBoundary";

function AnnouncementsInner() {
  const { openModal, closeModal, toast } = useApp();
  const role = useRole();
  const canManage = role === "owner" || role === "admin" || role === "manager";

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pinFilter, setPinFilter] = useState(""); // "" = all, "true" = pinned, "false" = unpinned

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filters = useMemo(
    () => ({
      page,
      limit: 10,
      ...(search && { search }),
      ...(pinFilter && { pinned: pinFilter }),
      sort: "createdAt",
      dir: "desc",
    }),
    [page, search, pinFilter],
  );

  const { announcements, total, loading, error, refresh } = useAnnouncements(filters);

  // ═══════════════════════════════════════════════════════════════
  // Modal-based create/edit form
  // ═══════════════════════════════════════════════════════════════
  const openCreate = () => {
    openModal(
      <AnnouncementFormModal
        onSaved={() => { refresh(); toast("Announcement created", "success"); }}
        onClose={() => closeModal()}
      />,
      "lg",
    );
  };

  const openEdit = (announcement) => {
    openModal(
      <AnnouncementFormModal
        announcement={announcement}
        onSaved={() => { refresh(); toast("Announcement updated", "success"); }}
        onClose={() => closeModal()}
      />,
      "lg",
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // Actions
  // ═══════════════════════════════════════════════════════════════
  const handlePin = async (id, pinned) => {
    try {
      if (pinned) await unpinAnnouncement(id);
      else await pinAnnouncement(id);
      refresh();
      toast(pinned ? "Announcement unpinned" : "Announcement pinned", "success");
    } catch (e) {
      toast(e.message || "Action failed", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try {
      await deleteAnnouncement(id);
      refresh();
      toast("Announcement deleted", "success");
    } catch (e) {
      toast(e.message || "Delete failed", "error");
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Helper: expiry status
  // ═══════════════════════════════════════════════════════════════
  const expiryInfo = (a) => {
    if (!a.expiresAt) return null;
    const d = new Date(a.expiresAt);
    const now = new Date();
    const days = Math.ceil((d - now) / 86400000);
    if (days < 0) return { label: "Expired", className: "badge-error" };
    if (days <= 3) return { label: `${days}d left`, className: "badge-warning" };
    return { label: d.toLocaleDateString(), className: "badge-info" };
  };

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════
  if (error) throw error;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Announcements</h1>
          <p className="page-sub">{total} announcement{total === 1 ? "" : "s"}</p>
        </div>
        {canManage && (
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <Icon name="plus" size={16} /> New Announcement
          </button>
        )}
      </div>

      {/* Search + Filter bar */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="toolbar" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <Icon name="search" size={14}
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
            />
            <input
              className="input"
              placeholder="Search announcements…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ paddingLeft: 30 }}
              aria-label="Search announcements"
            />
          </div>
          <button
            className={`filter-chip ${pinFilter === "" ? "active" : ""}`}
            onClick={() => { setPinFilter(""); setPage(1); }}
          >
            All
          </button>
          <button
            className={`filter-chip ${pinFilter === "true" ? "active" : ""}`}
            onClick={() => { setPinFilter("true"); setPage(1); }}
          >
            <Icon name="pin" size={12} /> Pinned
          </button>
          <button
            className={`filter-chip ${pinFilter === "false" ? "active" : ""}`}
            onClick={() => { setPinFilter("false"); setPage(1); }}
          >
            Unpinned
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="card" style={{ padding: 24, color: "var(--text-muted)" }}>Loading announcements…</div>
      ) : announcements.length === 0 ? (
        <EmptyState
          title="No announcements yet"
          icon="megaphone"
          desc={canManage ? "Create your first announcement to keep your team informed." : "Check back later for company announcements."}
          cta={canManage ? "New Announcement" : undefined}
          onCta={canManage ? openCreate : undefined}
        />
      ) : (
        <div className="ann-list">
          {announcements.map((a, i) => {
            const expiry = expiryInfo(a);
            return (
              <div
                key={a.id}
                className={`card ann-card ${a.pinned ? "pinned" : ""}`}
                style={{ animationDelay: `${Math.min(i * 50, 300)}ms` }}
              >
                {/* Leading icon tile */}
                <div className="ann-icon" title={a.pinned ? "Pinned" : undefined}>
                  <Icon name={a.pinned ? "pin" : "megaphone"} size={18} />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <h3 className="ann-title">{a.title}</h3>
                    {a.pinned && (
                      <span className="badge badge-primary" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Icon name="pin" size={11} /> Pinned
                      </span>
                    )}
                    {a.scheduledAt && !a.dispatchedAt && (
                      <span className="badge badge-warning" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Icon name="clock" size={11} /> Scheduled ·{" "}
                        {new Date(a.scheduledAt).toLocaleString("en-IN", {
                          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    )}
                    {a.dispatchedAt && (
                      <span className="badge badge-success" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Icon name="check" size={11} /> Sent
                      </span>
                    )}
                    <span className="badge badge-info" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Icon name="users" size={11} />{" "}
                      {a.recipientCount > 0 ? `To ${a.recipientCount} ${a.recipientCount === 1 ? "person" : "people"}` : "Everyone"}
                    </span>
                    {expiry && <span className={`badge ${expiry.className}`}>{expiry.label}</span>}
                  </div>

                  <div className="ann-body">{a.body}</div>

                  <div className="ann-meta">
                    <Avatar name={a.author?.name || "—"} size="sm" seed={(a.author?.name || "").length} />
                    <span style={{ fontWeight: 600, color: "var(--text)" }}>{a.author?.name || "—"}</span>
                    <span className="dot" />
                    <span>
                      {new Date(a.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                {canManage && (
                  <div className="ann-actions">
                    {a.pinned ? (
                      <button className="icon-btn" onClick={() => handlePin(a.id, true)} title="Unpin">
                        <Icon name="pin-off" size={14} />
                      </button>
                    ) : (
                      <button className="icon-btn" onClick={() => handlePin(a.id, false)} title="Pin">
                        <Icon name="pin" size={14} />
                      </button>
                    )}
                    <button className="icon-btn" onClick={() => openEdit(a)} title="Edit">
                      <Icon name="edit-2" size={14} />
                    </button>
                    <button className="icon-btn" onClick={() => handleDelete(a.id)} title="Delete" style={{ color: "var(--error)" }}>
                      <Icon name="trash-2" size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 10 && (
        <Pagination
          page={page}
          pages={Math.ceil(total / 10)}
          total={total}
          from={(page - 1) * 10 + 1}
          to={Math.min(page * 10, total)}
          pageSize={10}
          onPage={setPage}
        />
      )}
    </section>
  );
}

export default function Announcements() {
  return <PageBoundary><AnnouncementsInner /></PageBoundary>;
}
